import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../components/layout/DashboardShell";
import { Card, Button, Input, Pill } from "../components/common/Primitives";
import api, { formatApiError, API_BASE } from "../lib/api";
import { formatDateTime } from "../lib/datetime";
import { Plus, Send, X, Copy, ExternalLink, Trash2, Eye, CheckCircle2, Clock, Bell, Users, BellRing, FileText, History, BookMarked, Save, GitCompare, Filter, Upload, Layers, Search, MessageSquare, AlertTriangle, CheckSquare, RefreshCw, Reply } from "lucide-react";

const DOC_TYPES = ["loi", "term_sheet", "sha", "nda", "custom", "sales_agreement", "employment", "vendor"];
const MAX_REMINDERS = 3;

const fmt = (iso) => (iso ? formatDateTime(iso) : null);

export default function EsignDashboard() {
  const [envelopes, setEnvelopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [staleCount, setStaleCount] = useState(0);
  const [staleIds, setStaleIds] = useState(new Set());
  const [pricing, setPricing] = useState(null);

  const loadPricing = () => {
    api.get("/esign/pricing/me")
      .then((r) => setPricing(r.data))
      .catch(() => {});
  };
  useEffect(loadPricing, []);

  const load = () => {
    setLoading(true);
    const params = {};
    if (query.trim()) params.q = query.trim();
    if (statusFilter !== "all") params.status = statusFilter;
    api.get("/esign/envelopes", { params })
      .then((r) => setEnvelopes(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
    // Fire-and-forget: fetch stale envelopes for the banner
    api.get("/esign/envelopes/stale")
      .then((r) => {
        const items = r.data.items || [];
        setStaleCount(items.length);
        setStaleIds(new Set(items.map((e) => e.envelope_id)));
      }).catch(() => {});
  };

  useEffect(load, []);
  // Debounced re-fetch when query or statusFilter changes
  useEffect(() => {
    const h = setTimeout(load, 250);
    return () => clearTimeout(h);
  }, [query, statusFilter]);

  // Server-side filtering — envelopes already reflect the current query/status.
  const filtered = envelopes;

  // Per-status counts derived from currently-loaded set (approximate but okay
  // since the server also returns full counts when unfiltered).
  const counts = envelopes.reduce((acc, e) => {
    acc.all = (acc.all || 0) + 1;
    acc[e.status] = (acc[e.status] || 0) + 1;
    return acc;
  }, {});

  const STATUS_CHIPS = [
    { key: "all", label: "All" },
    { key: "draft", label: "Draft" },
    { key: "sent", label: "Sent" },
    { key: "in_progress", label: "In-Progress" },
    { key: "completed", label: "Completed" },
    { key: "voided", label: "Voided" },
  ];

  return (
    <DashboardShell title="E-Sign" subtitle="AGREEMENTS · SIGNATURES">
      <Toaster richColors position="top-right" />

      {pricing && (
        <div
          className="mb-4 rounded-md border border-navy/10 bg-gradient-to-r from-navy/[0.03] to-gold/[0.06] p-3 flex flex-wrap items-center justify-between gap-3"
          data-testid="esign-pricing-banner"
        >
          <div className="text-sm text-ink">
            <span className="text-[10px] font-mono uppercase tracking-[0.24em] text-ink-muted mr-2">FEE PER SIGN</span>
            <span className="font-heading text-lg text-gold" data-testid="esign-price-per-doc">
              ₹{Number(pricing.price_inr || 0).toLocaleString("en-IN")}
            </span>
            {pricing.source === "user_override" && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[9px] font-mono uppercase tracking-widest">custom rate</span>
            )}
          </div>
          <div className="text-xs font-mono text-ink-muted flex items-center gap-4">
            <span data-testid="esign-wallet-balance">
              Wallet: <span className="text-ink">₹{Number(pricing.wallet_balance_inr || 0).toLocaleString("en-IN")}</span>
              <span className="ml-1">· ~{pricing.signs_remaining ?? 0} sign{(pricing.signs_remaining ?? 0) === 1 ? "" : "s"} left</span>
            </span>
            <a
              href="/wallet"
              className="text-navy hover:text-gold underline decoration-gold decoration-2 underline-offset-4 uppercase tracking-widest text-[10px] font-heading"
              data-testid="esign-recharge-link"
            >
              Recharge →
            </a>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="overline">Envelopes · {envelopes.length}</div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowBulk(true)} data-testid="esign-bulk-btn"><Users size={14} className="mr-1" /> Bulk NDA</Button>
          <Button onClick={() => setShowCreate(true)} data-testid="esign-new-btn"><Plus size={14} className="mr-1" /> New Envelope</Button>
        </div>
      </div>

      {staleCount > 0 && (
        <div className="mb-4 border border-amber-400/40 bg-amber-50 text-amber-900 p-3 flex items-center justify-between gap-2 flex-wrap" data-testid="esign-stale-banner">
          <div className="flex items-center gap-2 text-sm">
            <AlertTriangle size={14} className="text-amber-600" />
            <span><b>{staleCount}</b> envelope{staleCount === 1 ? "" : "s"} stalled — signers ignored 3 reminders across 14+ days. Consider voiding or re-issuing.</span>
          </div>
          <button onClick={() => setStatusFilter("sent")}
                    className="text-xs font-mono uppercase tracking-widest text-amber-800 hover:text-amber-900" data-testid="esign-stale-review">
            Review sent
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search title, party name, email, or envelope ID…"
                  className="w-full pl-7 pr-8 py-2 border border-ink-line/40 bg-white text-sm text-ink"
                  data-testid="esign-search-input" />
          {query && (
            <button onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink" data-testid="esign-search-clear">
              <X size={12} />
            </button>
          )}
        </div>
        {STATUS_CHIPS.map((c) => (
          <button key={c.key} onClick={() => setStatusFilter(c.key)}
                    className={`text-xs font-mono uppercase tracking-widest px-2.5 py-1.5 border ${statusFilter === c.key ? "border-gold bg-gold/10 text-gold" : "border-ink-line/40 bg-white text-ink-muted hover:text-ink"}`}
                    data-testid={`esign-filter-${c.key}`}>
            {c.label} · {counts[c.key === "all" ? "all" : c.key] || 0}
          </button>
        ))}
      </div>

      {loading ? <div className="text-ink-muted text-sm">Loading…</div> : (
        <div className="grid gap-4" data-testid="esign-list">
          {envelopes.length === 0 && <Card><div className="text-ink-muted text-sm">No envelopes yet. Create your first one.</div></Card>}
          {envelopes.length > 0 && filtered.length === 0 && (
            <Card>
              <div className="text-ink-muted text-sm" data-testid="esign-list-empty-filter">
                No envelopes match your search or filter. <button onClick={() => { setQuery(""); setStatusFilter("all"); }} className="text-gold underline">Clear</button>
              </div>
            </Card>
          )}
          {filtered.map((env) => (
            <EnvelopeCard key={env.envelope_id} env={env} onChange={load} isStale={staleIds.has(env.envelope_id)} />
          ))}
        </div>
      )}

      {showCreate && <CreateEnvelopeModal onClose={() => setShowCreate(false)} onCreated={load} />}
      {showBulk && <BulkNDAModal onClose={() => setShowBulk(false)} onCreated={load} />}
    </DashboardShell>
  );
}

function statusTone(s) {
  return {
    draft: "muted", sent: "gold", in_progress: "gold",
    completed: "green", voided: "red",
  }[s] || "muted";
}

function EnvelopeCard({ env, onChange, isStale = false }) {
  const [busy, setBusy] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const comments = env.signer_comments || [];
  const openComments = comments.filter((c) => !c.resolved);
  const isInitiator = env.initiator_id;
  const canSend = env.status === "draft";
  const canVoid = env.status !== "completed" && env.status !== "voided";
  const canRemind = env.status === "sent" || env.status === "in_progress";

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/esign/envelopes/${env.envelope_id}/send`);
      const failed = (r.data?.deliveries || []).filter((d) => !d.sent);
      if (r.data?.all_sent) {
        toast.success("Envelope sent — signers notified via email.");
      } else if (failed.length === (r.data?.deliveries || []).length) {
        toast.error(
          "Envelope status flipped to Sent, but the invitation email failed for ALL signers. " +
          "Use the copy-link button next to each signer to share manually. " +
          `Reason: ${failed[0]?.reason?.slice(0, 120) || "unknown"}`,
          { duration: 10000 },
        );
      } else {
        toast.warning(
          `Envelope sent — but ${failed.length} of ${r.data.deliveries.length} email(s) failed. ` +
          `Use the copy-link fallback for those recipients.`,
          { duration: 8000 },
        );
      }
      onChange();
    } catch (e) {
      // Special-case insufficient wallet balance → hand the user a one-tap recharge path.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && detail?.error === "insufficient_wallet_balance") {
        toast.error(detail.message || "Wallet balance too low to send.", {
          duration: 12000,
          action: {
            label: "Recharge wallet",
            onClick: () => { window.location.href = detail.recharge_url || "/wallet"; },
          },
        });
      } else {
        toast.error(formatApiError(e));
      }
    }
    finally { setBusy(false); }
  };
  const resend = async (party) => {
    setBusy(true);
    try {
      const r = await api.post(`/esign/envelopes/${env.envelope_id}/resend`, { party_id: party.party_id });
      const d = (r.data?.deliveries || [])[0];
      if (d?.sent) toast.success(`Invitation re-sent to ${party.email}.`);
      else toast.error(`Re-send still failed for ${party.email}: ${d?.reason || "unknown"}`, { duration: 8000 });
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const voidEnv = async () => {
    if (!window.confirm("Void this envelope? This cannot be undone.")) return;
    setBusy(true);
    try { await api.post(`/esign/envelopes/${env.envelope_id}/void`, { reason: "cancelled by initiator" }); toast.success("Voided."); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const copyLink = (token) => {
    const url = `${window.location.origin}/esign/sign/${env.envelope_id}?t=${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Signing link copied");
  };
  const remind = async (party) => {
    setBusy(true);
    try {
      await api.post(`/esign/envelopes/${env.envelope_id}/remind/${party.party_id}`);
      toast.success(`Reminder sent to ${party.name}`);
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const reissue = async () => {
    if (!window.confirm("Clone this envelope into a new draft and void the original?")) return;
    setBusy(true);
    try {
      const r = await api.post(`/esign/envelopes/${env.envelope_id}/reissue`, { void_original: true });
      toast.success(`Re-issued as ${r.data.envelope.envelope_id}`);
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/esign/envelopes/${env.envelope_id}/download`, { responseType: "blob" });
      const disposition = r.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(disposition);
      const stem = (env.doc_number || env.envelope_id).replace(/\//g, "-");
      const fname = match?.[1] || `${stem}${env.signed_pdf_path ? "_signed" : "_draft"}.pdf`;
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click();
      a.remove(); setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card testId={`esign-card-${env.envelope_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-xs text-ink-muted font-mono uppercase tracking-widest">{env.document_type}</div>
          <div className="font-heading text-ink text-lg mt-1">{env.title}</div>
          <div className="text-xs text-ink-muted mt-1 flex items-center gap-2 flex-wrap" data-testid={`esign-envelope-header-${env.envelope_id}`}>
            {env.doc_number && (
              <span className="font-mono text-gold" data-testid={`esign-doc-number-${env.envelope_id}`}>{env.doc_number}</span>
            )}
            <span>· Envelope {env.envelope_id}</span>
            <span>· Created {formatDateTime(env.created_at)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isStale && (
            <span className="text-[9px] font-mono uppercase tracking-widest text-amber-800 bg-amber-100 border border-amber-300 px-1.5 py-0.5 flex items-center gap-1" data-testid={`esign-stale-chip-${env.envelope_id}`}>
              <AlertTriangle size={10} /> STALE
            </span>
          )}
          {openComments.length > 0 && (
            <span className="text-[9px] font-mono uppercase tracking-widest text-red-800 bg-red-100 border border-red-300 px-1.5 py-0.5 flex items-center gap-1" data-testid={`esign-open-comments-chip-${env.envelope_id}`}>
              <MessageSquare size={10} /> {openComments.length} open
            </span>
          )}
          <Pill tone={statusTone(env.status)}>{env.status}</Pill>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
        {env.parties.map((p, i) => (
          <div key={i} className="border border-white/10 bg-white/40 p-2 flex flex-col gap-1 text-sm" data-testid={`esign-envelope-party-${i}`}>
            <div className="flex justify-between items-center">
              <div>
                <div className="text-ink">{p.name} <span className="text-[10px] text-ink-muted">({p.role})</span></div>
                <div className="text-[11px] text-ink-muted">{p.email}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-mono uppercase tracking-widest ${p.status === "signed" ? "text-gold" : "text-ink-muted"}`} data-testid={`esign-party-status-${i}`}>{p.status}</span>
                {p.access_token && (
                  <button title="Copy signing link" onClick={() => copyLink(p.access_token)} className="text-ink-muted hover:text-ink" data-testid={`esign-copy-link-${i}`}>
                    <Copy size={12} />
                  </button>
                )}
                {canRemind && p.status !== "signed" && (p.reminder_count || 0) < MAX_REMINDERS && (
                  <button title="Send reminder now" onClick={() => remind(p)} disabled={busy}
                            className="text-ink-muted hover:text-gold disabled:opacity-50" data-testid={`esign-remind-btn-${i}`}>
                    <BellRing size={12} />
                  </button>
                )}
                {canRemind && p.status !== "signed" && p.email_status === "failed" && (
                  (p.resend_count_24h || 0) >= 5 ? (
                    <span
                      title="Resend cap reached (5/5 in the last 24h). Try again in 24h or void + re-issue."
                      className="text-[10px] font-mono text-rose-800 bg-rose-100 border border-rose-200 rounded px-1.5 py-0.5"
                      data-testid={`esign-resend-capped-${i}`}
                    >
                      5/5 · capped
                    </span>
                  ) : (
                    <button
                      title={`Email delivery failed: ${p.email_reason || "unknown"} — click to retry (${p.resend_count_24h || 0}/5 used in last 24h)`}
                      onClick={() => resend(p)} disabled={busy}
                      className="text-rose-700 hover:text-rose-900 disabled:opacity-50"
                      data-testid={`esign-resend-btn-${i}`}
                    >
                      <RefreshCw size={12} />
                    </button>
                  )
                )}
              </div>
            </div>
            {p.email_status === "failed" && (
              <div className="mt-1 text-[10px] font-mono text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-1"
                     data-testid={`esign-email-failed-${i}`}>
                Email delivery failed{p.email_reason ? `: ${p.email_reason.slice(0, 100)}` : ""}. Use copy-link to share manually or click retry.
              </div>
            )}
            {p.email_status === "sent" && (
              <div className="mt-1 text-[10px] font-mono text-emerald-700"
                     data-testid={`esign-email-sent-${i}`}>
                Invitation email sent{p.email_last_attempt_at ? ` · ${p.email_last_attempt_at.slice(0, 16)}` : ""}
              </div>
            )}
            <PartyTimeline party={p} envSentAt={env.sent_at} testIdx={i} />
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {canSend && <Button onClick={send} disabled={busy} data-testid="esign-send-btn"><Send size={12} className="mr-1" /> Send</Button>}
        {env.status !== "voided" && (
          <button onClick={downloadPdf} disabled={busy}
                  className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-gold hover:text-gold/70 disabled:opacity-50"
                  data-testid={env.signed_pdf_path ? "esign-download-signed" : "esign-download-draft"}>
            <ExternalLink size={12} /> {env.signed_pdf_path ? "Download Signed PDF" : "Download Draft PDF"}
          </button>
        )}
        {comments.length > 0 && (
          <button onClick={() => setShowComments(!showComments)}
                    className="text-xs text-ink-muted hover:text-ink flex items-center gap-1"
                    data-testid={`esign-comments-toggle-${env.envelope_id}`}>
            <MessageSquare size={12} /> Comments · {comments.length}
          </button>
        )}
        <button onClick={() => setShowVersions(true)}
                  className="text-xs text-ink-muted hover:text-ink flex items-center gap-1"
                  data-testid="esign-versions-btn">
          <History size={12} /> Versions
        </button>
        {canVoid && <button onClick={voidEnv} disabled={busy} className="text-xs text-ink-muted hover:text-red-400 flex items-center gap-1"><Trash2 size={12} /> Void</button>}
        {(isStale || env.status === "voided") && env.status !== "completed" && (
          <button onClick={reissue} disabled={busy}
                    className="text-xs text-gold hover:text-gold/70 font-mono uppercase tracking-widest flex items-center gap-1"
                    data-testid={`esign-reissue-btn-${env.envelope_id}`}>
            <RefreshCw size={12} /> Re-issue
          </button>
        )}
      </div>
      {showComments && comments.length > 0 && (
        <div className="mt-3 border-t border-ink-line/20 pt-3 space-y-2" data-testid={`esign-comments-list-${env.envelope_id}`}>
          {comments.slice().reverse().map((c) => (
            <CommentThread key={c.comment_id} c={c} envelopeId={env.envelope_id} onChange={onChange} />
          ))}
        </div>
      )}
      {showVersions && <VersionHistoryModal envelopeId={env.envelope_id} title={env.title} onClose={() => setShowVersions(false)} />}
    </Card>
  );
}

function PartyTimeline({ party, envSentAt, testIdx }) {
  const sent = fmt(envSentAt);
  const viewed = fmt(party.viewed_at);
  const signed = fmt(party.signed_at);
  const lastRem = fmt(party.last_reminded_at);
  const rc = party.reminder_count || 0;
  return (
    <div className="flex flex-wrap items-center gap-3 text-[10px] text-ink-muted font-mono uppercase tracking-wider pt-1 border-t border-white/10" data-testid={`esign-party-timeline-${testIdx}`}>
      <span className="flex items-center gap-1" title={envSentAt || "Not sent yet"}>
        <Send size={10} className={sent ? "text-ink" : "text-ink-muted/50"} />
        {sent || "—"}
      </span>
      <span className="flex items-center gap-1" title={party.viewed_at || "Not opened"}>
        <Eye size={10} className={viewed ? "text-ink" : "text-ink-muted/50"} />
        {viewed || "—"}
      </span>
      <span className="flex items-center gap-1" title={party.signed_at || "Not signed"}>
        <CheckCircle2 size={10} className={signed ? "text-gold" : "text-ink-muted/50"} />
        {signed || "—"}
      </span>
      {rc > 0 && (
        <span className="flex items-center gap-1 text-amber-600" title={`Last reminder: ${lastRem || "—"}`} data-testid={`esign-reminder-badge-${testIdx}`}>
          <Bell size={10} /> {rc}
        </span>
      )}
      {!signed && !viewed && sent && (
        <span className="flex items-center gap-1" title="Waiting on signer"><Clock size={10} /> pending</span>
      )}
    </div>
  );
}

function CommentThread({ c, envelopeId, onChange }) {
  const [replyText, setReplyText] = useState("");
  const [showReply, setShowReply] = useState(false);
  const [busy, setBusy] = useState(false);
  const replies = c.replies || [];

  const resolve = async () => {
    setBusy(true);
    try {
      await api.post(`/esign/envelopes/${envelopeId}/comments/${c.comment_id}/resolve`);
      toast.success("Marked resolved");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const postReply = async () => {
    if (!replyText.trim()) return;
    setBusy(true);
    try {
      await api.post(`/esign/envelopes/${envelopeId}/comments/${c.comment_id}/reply`,
                        { body: replyText.trim() });
      setReplyText(""); setShowReply(false);
      toast.success("Reply posted");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className={`p-2 border ${c.resolved ? "border-white/10 bg-white/30" : "border-amber-300 bg-amber-50"}`}
          data-testid={`esign-comment-${c.comment_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-ink-muted font-mono">
            <b className="text-ink">{c.party_name}</b> · {formatDateTime(c.created_at)}
          </div>
          <div className="text-sm text-ink mt-1 whitespace-pre-wrap">{c.body}</div>
          {c.resolved && (
            <div className="text-[10px] text-gold mt-1 font-mono uppercase tracking-widest">
              ✓ Resolved by {c.resolved_by} · {c.resolved_at && formatDateTime(c.resolved_at)}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => setShowReply(!showReply)}
                    className="text-xs font-mono uppercase tracking-widest text-ink-muted hover:text-ink flex items-center gap-1"
                    data-testid={`esign-comment-reply-btn-${c.comment_id}`}>
            <Reply size={12} /> Reply
          </button>
          {!c.resolved && (
            <button onClick={resolve} disabled={busy}
                      className="text-xs font-mono uppercase tracking-widest text-gold hover:text-gold/70 flex items-center gap-1"
                      data-testid={`esign-comment-resolve-${c.comment_id}`}>
              <CheckSquare size={12} /> Resolve
            </button>
          )}
        </div>
      </div>
      {replies.length > 0 && (
        <div className="mt-2 ml-4 pl-3 border-l-2 border-ink-line/30 space-y-2" data-testid={`esign-comment-replies-${c.comment_id}`}>
          {replies.map((r) => (
            <div key={r.reply_id} className="text-xs" data-testid={`esign-reply-${r.reply_id}`}>
              <div className="text-[11px] text-ink-muted font-mono">
                <b className="text-ink">{r.author_name || r.author_email}</b>{" "}
                <span className={`text-[9px] font-mono uppercase tracking-widest ${r.author_role === "initiator" ? "text-gold" : r.author_role === "admin" ? "text-blue-600" : "text-ink-muted"}`}>[{r.author_role}]</span>
                {" · "}{formatDateTime(r.created_at)}
              </div>
              <div className="text-sm text-ink mt-1 whitespace-pre-wrap">{r.body}</div>
            </div>
          ))}
        </div>
      )}
      {showReply && (
        <div className="mt-2 space-y-2">
          <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={3}
                      placeholder="Reply to this comment…"
                      className="w-full px-2 py-1.5 border border-ink-line/40 bg-white text-sm"
                      data-testid={`esign-comment-reply-textarea-${c.comment_id}`} />
          <div className="flex items-center justify-end gap-2">
            <button onClick={() => { setShowReply(false); setReplyText(""); }}
                      className="text-xs font-mono uppercase tracking-widest text-ink-muted">Cancel</button>
            <button onClick={postReply} disabled={busy || !replyText.trim()}
                      className="bg-ink text-white text-xs font-mono uppercase tracking-widest px-3 py-1.5 disabled:opacity-50"
                      data-testid={`esign-comment-reply-submit-${c.comment_id}`}>
              Post reply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function VersionHistoryModal({ envelopeId, title, onClose }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null);
  const [previewVer, setPreviewVer] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [diffResult, setDiffResult] = useState(null);
  const [diffBusy, setDiffBusy] = useState(false);

  useEffect(() => {
    api.get(`/esign/envelopes/${envelopeId}/versions`)
      .then((r) => { setVersions(r.data.versions || []); setCurrent(r.data.current_version_no); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [envelopeId]);

  const preview = async (verNo) => {
    try {
      const r = await api.get(`/esign/envelopes/${envelopeId}/versions/${verNo}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      setPreviewVer(verNo);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null); setPreviewVer(null);
  };

  const toggleSelect = (verNo) => {
    setSelected((cur) => {
      if (cur.includes(verNo)) return cur.filter((v) => v !== verNo);
      if (cur.length >= 2) return [cur[1], verNo]; // keep last 2
      return [...cur, verNo];
    });
  };

  const runDiff = async () => {
    if (selected.length !== 2) { toast.error("Pick exactly 2 versions."); return; }
    const [a, b] = [...selected].sort((x, y) => x - y);
    setDiffBusy(true);
    try {
      const r = await api.get(`/esign/envelopes/${envelopeId}/versions/diff`, {
        params: { from: a, to: b },
      });
      setDiffResult(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDiffBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/70 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white/95 border border-gold/40 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="esign-versions-modal">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Document Versions</div>
              <div className="text-xs text-ink-muted mt-1 truncate max-w-md">{title}</div>
            </div>
            <div className="flex items-center gap-3">
              {versions.length >= 2 && (
                <button onClick={() => { setCompareMode(!compareMode); setSelected([]); }}
                          className={`text-xs font-mono uppercase tracking-widest flex items-center gap-1 ${compareMode ? "text-gold" : "text-ink-muted hover:text-ink"}`}
                          data-testid="esign-version-compare-toggle">
                  <GitCompare size={12} /> {compareMode ? "Cancel compare" : "Compare"}
                </button>
              )}
              <button onClick={onClose} className="text-ink-muted hover:text-ink"><X size={16} /></button>
            </div>
          </div>
          {compareMode && (
            <div className="mb-3 p-2 border border-gold/40 bg-gold/5 text-xs text-ink flex items-center justify-between">
              <span data-testid="esign-version-compare-hint">
                Select 2 versions to redline · {selected.length}/2 chosen
              </span>
              <Button onClick={runDiff} disabled={selected.length !== 2 || diffBusy} data-testid="esign-version-run-diff">
                {diffBusy ? "Comparing…" : "Show Redline"}
              </Button>
            </div>
          )}
          {loading ? <div className="text-ink-muted text-sm">Loading…</div> : (
            versions.length === 0 ? <div className="text-ink-muted text-sm">No versions recorded yet.</div> : (
              <div className="space-y-2">
                {versions.slice().reverse().map((v) => (
                  <div key={v.version_no} className={`border p-3 flex flex-wrap items-center justify-between gap-2 ${selected.includes(v.version_no) ? "border-gold bg-gold/5" : "border-white/10 bg-white/40"}`} data-testid={`esign-version-${v.version_no}`}>
                    <div className="flex items-start gap-2 flex-1 min-w-[200px]">
                      {compareMode && (
                        <input type="checkbox" checked={selected.includes(v.version_no)}
                                onChange={() => toggleSelect(v.version_no)}
                                className="mt-1" data-testid={`esign-version-select-${v.version_no}`} />
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono uppercase tracking-widest text-ink">v{v.version_no}</span>
                          {v.version_no === current && <span className="text-[9px] font-mono uppercase tracking-widest text-gold bg-gold/10 px-1.5 py-0.5" data-testid="esign-version-current">CURRENT</span>}
                        </div>
                        <div className="text-[11px] text-ink-muted mt-1">
                          {formatDateTime(v.uploaded_at)} · {(v.size/1024).toFixed(1)} KB
                        </div>
                        <div className="text-[11px] text-ink-muted">by {v.uploaded_by}</div>
                        {v.note && <div className="text-[11px] text-ink mt-1 italic">&ldquo;{v.note}&rdquo;</div>}
                      </div>
                    </div>
                    <button onClick={() => preview(v.version_no)}
                              className="text-xs font-mono uppercase tracking-widest text-gold hover:text-gold/70 flex items-center gap-1"
                              data-testid={`esign-version-view-${v.version_no}`}>
                      <Eye size={12} /> View PDF
                    </button>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
      {previewUrl && (
        <div className="fixed inset-0 bg-ink/85 z-[60] flex items-center justify-center p-6" onClick={closePreview}>
          <div className="bg-white w-full max-w-5xl h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="esign-version-preview-modal">
            <div className="flex items-center justify-between px-4 py-2 border-b border-ink-line/20 bg-ink text-white">
              <div className="overline text-gold">Version {previewVer} · {title}</div>
              <div className="flex items-center gap-3">
                <a href={previewUrl} target="_blank" rel="noreferrer" className="text-xs font-mono uppercase tracking-widest text-gold hover:text-gold/70 flex items-center gap-1">
                  <ExternalLink size={12} /> Open in tab
                </a>
                <button onClick={closePreview} className="text-white/70 hover:text-white"><X size={16} /></button>
              </div>
            </div>
            <iframe title={`Version ${previewVer}`} src={previewUrl} className="flex-1 w-full" />
          </div>
        </div>
      )}
      {diffResult && <VersionDiffOverlay data={diffResult} title={title} onClose={() => setDiffResult(null)} />}
    </div>
  );
}

function VersionDiffOverlay({ data, title, onClose }) {
  const [mode, setMode] = useState("side"); // side | unified
  const { ops = [], stats = {}, from_label, to_label } = data;
  return (
    <div className="fixed inset-0 bg-ink/85 z-[65] flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white w-full max-w-6xl h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="esign-version-diff-modal">
        <div className="flex items-center justify-between px-4 py-2 border-b border-ink-line/20 bg-ink text-white gap-4">
          <div className="min-w-0">
            <div className="overline text-gold">Redline · {from_label} → {to_label}</div>
            <div className="text-xs text-white/70 truncate">{title}</div>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="text-green-300" data-testid="esign-diff-stat-added">+ {stats.added || 0} added</span>
            <span className="text-red-300" data-testid="esign-diff-stat-removed">− {stats.removed || 0} removed</span>
            <span className="text-white/60">{stats.unchanged || 0} unchanged</span>
            <div className="border-l border-white/20 h-4" />
            <button onClick={() => setMode("side")}
                      className={`font-mono uppercase tracking-widest ${mode === "side" ? "text-gold" : "text-white/60 hover:text-white"}`}>
              Side-by-side
            </button>
            <button onClick={() => setMode("unified")}
                      className={`font-mono uppercase tracking-widest ${mode === "unified" ? "text-gold" : "text-white/60 hover:text-white"}`}>
              Unified
            </button>
            <button onClick={onClose} className="text-white/70 hover:text-white ml-2"><X size={16} /></button>
          </div>
        </div>
        {stats.identical && (
          <div className="p-6 text-center text-ink-muted text-sm">The two versions are identical.</div>
        )}
        {!stats.identical && mode === "side" && (
          <div className="flex-1 grid grid-cols-2 gap-px bg-ink-line/20 overflow-hidden">
            <div className="bg-white overflow-y-auto text-xs font-mono">
              <div className="sticky top-0 bg-white/95 px-3 py-1 text-[10px] uppercase tracking-widest text-ink-muted border-b border-ink-line/20">{from_label}</div>
              {ops.map((o, i) => (
                <div key={i} className={
                  o.op === "removed" ? "bg-red-100 text-red-900 px-3 py-0.5" :
                  o.op === "replaced" && o.from !== null ? "bg-red-50 text-red-800 px-3 py-0.5" :
                  o.op === "added" ? "px-3 py-0.5 text-transparent" :
                  "px-3 py-0.5 text-ink"
                }>
                  {o.from ?? "\u00A0"}
                </div>
              ))}
            </div>
            <div className="bg-white overflow-y-auto text-xs font-mono">
              <div className="sticky top-0 bg-white/95 px-3 py-1 text-[10px] uppercase tracking-widest text-ink-muted border-b border-ink-line/20">{to_label}</div>
              {ops.map((o, i) => (
                <div key={i} className={
                  o.op === "added" ? "bg-green-100 text-green-900 px-3 py-0.5" :
                  o.op === "replaced" && o.to !== null ? "bg-green-50 text-green-800 px-3 py-0.5" :
                  o.op === "removed" ? "px-3 py-0.5 text-transparent" :
                  "px-3 py-0.5 text-ink"
                }>
                  {o.to ?? "\u00A0"}
                </div>
              ))}
            </div>
          </div>
        )}
        {!stats.identical && mode === "unified" && (
          <pre className="flex-1 bg-white overflow-auto text-[11px] font-mono p-4 whitespace-pre-wrap" data-testid="esign-diff-unified">
            {(data.unified_diff || "").split("\n").map((ln, i) => {
              const cls = ln.startsWith("+") && !ln.startsWith("+++") ? "text-green-700" :
                            ln.startsWith("-") && !ln.startsWith("---") ? "text-red-700" :
                            ln.startsWith("@@") ? "text-gold" : "text-ink";
              return <div key={i} className={cls}>{ln || "\u00A0"}</div>;
            })}
          </pre>
        )}
      </div>
    </div>
  );
}

function CreateEnvelopeModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState("loi");
  const [body, setBody] = useState("");
  const [parties, setParties] = useState([{ name: "", email: "", role: "signer", order: 1 }]);
  const [busy, setBusy] = useState(false);

  const addParty = () => setParties([...parties, { name: "", email: "", role: "signer", order: parties.length + 1 }]);
  const rmParty = (i) => setParties(parties.filter((_, ix) => ix !== i));
  const updateParty = (i, field, val) => {
    const next = [...parties]; next[i] = { ...next[i], [field]: val }; setParties(next);
  };

  const submit = async () => {
    if (!title.trim() || parties.some((p) => !p.name.trim() || !p.email.trim())) {
      toast.error("Enter a title and complete all party details."); return;
    }
    setBusy(true);
    try {
      await api.post("/esign/envelopes", { title, document_type: docType, body, parties });
      toast.success("Envelope created — send it to signers when ready.");
      onCreated(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/70 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white/95 border border-gold/40 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="esign-create-modal">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">Create Envelope</div>
            <button onClick={onClose} className="text-ink-muted hover:text-ink"><X size={16} /></button>
          </div>
          <div className="space-y-3">
            <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} testId="esign-create-title" />
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1 block">Document Type</label>
              <select value={docType} onChange={(e) => setDocType(e.target.value)}
                       className="w-full px-3 py-2 border border-ink-line/40 bg-white text-ink text-sm" data-testid="esign-create-doctype">
                {DOC_TYPES.map((d) => <option key={d} value={d}>{d.toUpperCase()}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1 block">Body (auto-generated PDF)</label>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4}
                          className="w-full px-3 py-2 border border-ink-line/40 bg-white text-ink text-sm" data-testid="esign-create-body"
                          placeholder="Terms of the agreement. You can also upload a real PDF after creating the envelope." />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Parties</label>
                <button onClick={addParty} className="text-xs text-gold hover:text-gold/70 flex items-center gap-1" data-testid="esign-add-party">
                  <Plus size={12} /> Add party
                </button>
              </div>
              <div className="space-y-2">
                {parties.map((p, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`esign-create-party-${i}`}>
                    <input value={p.name} onChange={(e) => updateParty(i, "name", e.target.value)} placeholder="Name"
                            className="col-span-4 px-2 py-2 border border-ink-line/40 bg-white text-sm" />
                    <input value={p.email} onChange={(e) => updateParty(i, "email", e.target.value)} placeholder="email@x.com"
                            className="col-span-4 px-2 py-2 border border-ink-line/40 bg-white text-sm" />
                    <input value={p.role} onChange={(e) => updateParty(i, "role", e.target.value)} placeholder="role"
                            className="col-span-2 px-2 py-2 border border-ink-line/40 bg-white text-sm" />
                    <input type="number" min="1" value={p.order} onChange={(e) => updateParty(i, "order", Number(e.target.value))}
                            className="col-span-1 px-2 py-2 border border-ink-line/40 bg-white text-sm" />
                    {parties.length > 1 && (
                      <button onClick={() => rmParty(i)} className="col-span-1 text-ink-muted hover:text-red-400"><X size={14} /></button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={onClose} className="text-xs text-ink-muted font-mono uppercase tracking-widest">Cancel</button>
            <Button onClick={submit} disabled={busy} data-testid="esign-create-submit">{busy ? "Creating…" : "Create Envelope"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}


function BulkNDAModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("NDA — Investor Outreach");
  const [purpose, setPurpose] = useState("evaluating a potential investment or strategic partnership");
  const [jurisdiction, setJurisdiction] = useState("New Delhi");
  const [termYears, setTermYears] = useState(3);
  const [initiatorName, setInitiatorName] = useState("");
  const [initiatorEmail, setInitiatorEmail] = useState("");
  const [initiatorEntity, setInitiatorEntity] = useState("Private Limited Company");
  const [initiatorAddress, setInitiatorAddress] = useState("");
  const [initiatorReg, setInitiatorReg] = useState("");
  const [recipientsText, setRecipientsText] = useState("");
  const [autoSend, setAutoSend] = useState(true);
  const [extraClauses, setExtraClauses] = useState([]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [library, setLibrary] = useState([]);
  const [libLoaded, setLibLoaded] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    api.get("/esign/pricing/me").then((r) => setPricing(r.data)).catch(() => {});
  }, []);

  const loadTemplates = () => {
    api.get("/esign/templates").then((r) => {
      setTemplates(r.data.items || []);
      setShowTemplates(true);
    }).catch((e) => toast.error(formatApiError(e)));
  };

  const applyTemplate = (t) => {
    if (t.title) setTitle(t.title);
    if (t.nda_params?.purpose) setPurpose(t.nda_params.purpose);
    if (t.nda_params?.jurisdiction_city) setJurisdiction(t.nda_params.jurisdiction_city);
    if (t.nda_params?.term_years) setTermYears(t.nda_params.term_years);
    if (t.extra_clauses?.length) setExtraClauses(t.extra_clauses.map((c) => ({ title: c.title, body: c.body })));
    setShowTemplates(false);
    toast.success(`Loaded template "${t.name}"`);
  };

  const saveAsTemplate = async () => {
    const name = window.prompt("Template name:", title || "My NDA template");
    if (!name?.trim()) return;
    try {
      const cleanClauses = extraClauses.filter((c) => (c.title || "").trim() && (c.body || "").trim());
      await api.post("/esign/templates", {
        name: name.trim(), title, document_type: "nda",
        parties_template: [
          { name_placeholder: initiatorName || "[Party 1]", role: "disclosing", order: 1 },
          { name_placeholder: "[Party 2]", role: "receiving", order: 2 },
        ],
        nda_params: {
          purpose, jurisdiction_city: jurisdiction, term_years: Number(termYears) || 3,
          initiator_entity: initiatorEntity,
        },
        extra_clauses: cleanClauses,
      });
      toast.success(`Saved template "${name}"`);
      setTemplates([]); // force re-fetch next time
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteTemplate = async (tid) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/esign/templates/${tid}`);
      setTemplates((cur) => cur.filter((t) => t.template_id !== tid));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onCsvUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const text = await f.text();
      const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      // Auto-detect header row
      const firstCells = lines[0].split(/[,;\t]/).map((s) => s.trim().toLowerCase());
      const hasHeader = firstCells.some((c) => c === "email" || c === "name");
      const rows = hasHeader ? lines.slice(1) : lines;
      const emailIdx = hasHeader ? firstCells.indexOf("email") : 1;
      const nameIdx = hasHeader ? firstCells.indexOf("name") : 0;
      const out = [];
      for (const line of rows) {
        const cells = line.split(/[,;\t]/).map((s) => s.trim().replace(/^"|"$/g, ""));
        const email = (emailIdx >= 0 && cells[emailIdx]) || cells.find((c) => /@/.test(c)) || "";
        const name = (nameIdx >= 0 && cells[nameIdx]) || email.split("@")[0];
        if (email && /@/.test(email)) out.push(`${name} <${email}>`);
      }
      if (out.length === 0) { toast.error("No valid rows found in CSV."); return; }
      setRecipientsText((cur) => (cur.trim() ? cur.trim() + "\n" : "") + out.join("\n"));
      toast.success(`Imported ${out.length} recipient(s) from CSV`);
    } catch (err) { toast.error("CSV parse failed"); }
    finally { e.target.value = ""; }
  };

  const loadLibrary = () => {
    if (libLoaded) { setShowLibrary(true); return; }
    api.get("/esign/clauses").then((r) => {
      setLibrary(r.data.items || []);
      setLibLoaded(true);
      setShowLibrary(true);
    }).catch((e) => toast.error(formatApiError(e)));
  };

  const insertFromLibrary = (c) => {
    setExtraClauses((cur) => [...cur, { title: c.title, body: c.body }]);
    toast.success(`Inserted "${c.title}"`);
  };

  const saveToLibrary = async (i) => {
    const c = extraClauses[i];
    if (!c?.title?.trim() || !c?.body?.trim()) { toast.error("Fill title and body first."); return; }
    try {
      const r = await api.post("/esign/clauses", { title: c.title.trim(), body: c.body.trim() });
      toast.success(`Saved "${r.data.clause.title}" to your library`);
      setLibrary((cur) => [...cur, r.data.clause]);
      setLibLoaded(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addClause = () => setExtraClauses([...extraClauses, { title: "", body: "" }]);
  const rmClause = (i) => setExtraClauses(extraClauses.filter((_, ix) => ix !== i));
  const updateClause = (i, field, val) => {
    const next = [...extraClauses]; next[i] = { ...next[i], [field]: val }; setExtraClauses(next);
  };

  // Prefill initiator from current user
  useEffect(() => {
    api.get("/auth/me").then((r) => {
      const u = r.data.user || r.data;
      if (u?.email && !initiatorEmail) setInitiatorEmail(u.email);
      if (u?.name && !initiatorName) setInitiatorName(u.name);
    }).catch(() => {});
  }, []);

  const parseRecipients = () => {
    // Accept lines like "Name <email@x.com>" or "Name, email@x.com" or plain "email@x.com"
    const rows = recipientsText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const out = [];
    for (const line of rows) {
      const m1 = line.match(/^(.*?)<([^>]+)>$/);
      const m2 = line.match(/^(.+?)[,;\t]\s*([^\s]+@[^\s]+)$/);
      const m3 = line.match(/^\s*([^\s]+@[^\s]+)\s*$/);
      if (m1) out.push({ name: m1[1].trim() || m1[2].split("@")[0], email: m1[2].trim(), role: "receiving" });
      else if (m2) out.push({ name: m2[1].trim(), email: m2[2].trim(), role: "receiving" });
      else if (m3) out.push({ name: m3[1].split("@")[0], email: m3[1].trim(), role: "receiving" });
    }
    return out;
  };

  const buildNdaParams = () => {
    const cleanClauses = extraClauses.filter((c) => (c.title || "").trim() && (c.body || "").trim());
    const params = {
      purpose, jurisdiction_city: jurisdiction, term_years: Number(termYears) || 3,
      initiator_entity: initiatorEntity, initiator_address: initiatorAddress,
      initiator_registration_no: initiatorReg,
    };
    if (cleanClauses.length) params.extra_clauses = cleanClauses;
    return params;
  };

  const preview = async () => {
    if (!initiatorName.trim() || !initiatorEmail.trim()) { toast.error("Enter your name and email first."); return; }
    setPreviewBusy(true);
    try {
      const recipients = parseRecipients();
      const body = {
        initiator_party: { name: initiatorName, email: initiatorEmail, role: "disclosing", order: 1 },
        recipient: recipients[0] || null,
        nda_params: buildNdaParams(),
      };
      const r = await api.post("/esign/nda/preview", body, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setPreviewBusy(false); }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const submit = async () => {
    const recipients = parseRecipients();
    if (!title.trim()) { toast.error("Enter a title."); return; }
    if (!initiatorName.trim() || !initiatorEmail.trim()) { toast.error("Enter your name and email as the disclosing party."); return; }
    if (recipients.length === 0) { toast.error("Add at least one recipient."); return; }
    setBusy(true);
    try {
      const r = await api.post("/esign/envelopes/bulk", {
        title, document_type: "nda",
        initiator_party: { name: initiatorName, email: initiatorEmail, role: "disclosing", order: 1 },
        recipients,
        nda_params: buildNdaParams(),
        auto_send: autoSend,
      });
      const { created = [], errors = [], bulk_discount_pct, total_charged_inr, total_recipients } = r.data || {};
      if (errors.length) {
        const total = total_recipients ?? (errors.length + created.length);
        // Show a rich, scrollable toast listing every failed recipient so a founder can retry them.
        const list = errors.slice(0, 10).map((e) => `• ${e.recipient_email}: ${(e.error || "unknown").slice(0, 120)}`).join("\n");
        const extra = errors.length > 10 ? `\n…and ${errors.length - 10} more.` : "";
        toast.error(
          `${errors.length} of ${total} envelope(s) failed`,
          {
            description: (
              <pre className="whitespace-pre-wrap text-xs leading-relaxed max-h-56 overflow-y-auto">{list + extra}</pre>
            ),
            duration: 20000,
          },
        );
        if (created.length) {
          toast.success(
            `${created.length} envelope${created.length === 1 ? "" : "s"} ${autoSend ? "sent" : "drafted"} successfully.`,
            { duration: 6000 },
          );
        }
      }
      else if (bulk_discount_pct > 0 && total_charged_inr) {
        toast.success(
          `${created.length} NDA${created.length === 1 ? "" : "s"} ${autoSend ? "sent" : "drafted"} · charged ₹${total_charged_inr} (${bulk_discount_pct}% bulk discount applied)`,
          { duration: 6000 },
        );
      } else {
        toast.success(`${created.length} NDA envelope${created.length === 1 ? "" : "s"} ${autoSend ? "sent" : "drafted"}`);
      }
      onCreated(); onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && detail?.error === "insufficient_wallet_balance") {
        toast.error(detail.message || "Wallet balance too low for this bulk send.", {
          duration: 12000,
          action: {
            label: "Recharge wallet",
            onClick: () => { window.location.href = detail.recharge_url || "/wallet"; },
          },
        });
      } else {
        toast.error(formatApiError(e));
      }
    }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/70 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white/95 border border-gold/40 max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="esign-bulk-modal">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Bulk NDA Send</div>
              <div className="text-xs text-ink-muted mt-1">Auto-generate a legally-tight mutual NDA and blast it to a roster of investors — one envelope per recipient, all signed independently.</div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={loadTemplates} className="text-xs text-ink hover:text-gold flex items-center gap-1" data-testid="esign-bulk-open-templates">
                <Layers size={12} /> Templates
              </button>
              <button onClick={saveAsTemplate} className="text-xs text-ink-muted hover:text-gold flex items-center gap-1" data-testid="esign-bulk-save-template">
                <Save size={12} /> Save as template
              </button>
              <button onClick={onClose} className="text-ink-muted hover:text-ink"><X size={16} /></button>
            </div>
          </div>

          <div className="space-y-3">
            <Input label="Title (recipient name will be appended)" value={title} onChange={(e) => setTitle(e.target.value)} testId="esign-bulk-title" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label="Your name / entity" value={initiatorName} onChange={(e) => setInitiatorName(e.target.value)} testId="esign-bulk-initiator-name" />
              <Input label="Your email" value={initiatorEmail} onChange={(e) => setInitiatorEmail(e.target.value)} testId="esign-bulk-initiator-email" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1 block">Entity type</label>
                <select value={initiatorEntity} onChange={(e) => setInitiatorEntity(e.target.value)}
                          className="w-full px-3 py-2 border border-ink-line/40 bg-white text-ink text-sm" data-testid="esign-bulk-entity">
                  {["Private Limited Company", "Public Limited Company", "LLP", "Partnership Firm", "Sole Proprietorship", "Individual", "Trust", "Society"].map((v) =>
                    <option key={v}>{v}</option>)}
                </select>
              </div>
              <Input label="Registration / CIN / PAN" value={initiatorReg} onChange={(e) => setInitiatorReg(e.target.value)} testId="esign-bulk-reg" />
              <Input label="Address" value={initiatorAddress} onChange={(e) => setInitiatorAddress(e.target.value)} testId="esign-bulk-address" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1 block">Purpose (recital)</label>
                <input value={purpose} onChange={(e) => setPurpose(e.target.value)}
                        className="w-full px-3 py-2 border border-ink-line/40 bg-white text-ink text-sm" data-testid="esign-bulk-purpose" />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1 block">Jurisdiction</label>
                <input value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)}
                        className="w-full px-3 py-2 border border-ink-line/40 bg-white text-ink text-sm" data-testid="esign-bulk-jurisdiction" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1 block">Term (years)</label>
                <input type="number" min="1" max="10" value={termYears} onChange={(e) => setTermYears(e.target.value)}
                        className="w-full px-3 py-2 border border-ink-line/40 bg-white text-ink text-sm" data-testid="esign-bulk-term" />
              </div>
              <label className="flex items-center gap-2 mt-6 col-span-2 text-sm text-ink">
                <input type="checkbox" checked={autoSend} onChange={(e) => setAutoSend(e.target.checked)} data-testid="esign-bulk-autosend" />
                Send immediately to all recipients (else keep as drafts)
              </label>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                  Recipients · one per line — <span className="normal-case text-ink-muted">accepts <code>Name &lt;email@x.com&gt;</code>, <code>Name, email@x.com</code>, or bare email</span>
                </label>
                <label className="text-xs text-ink hover:text-gold flex items-center gap-1 cursor-pointer" data-testid="esign-bulk-csv-label">
                  <Upload size={12} /> Import CSV
                  <input type="file" accept=".csv,text/csv" onChange={onCsvUpload} className="hidden" data-testid="esign-bulk-csv-input" />
                </label>
              </div>
              <textarea value={recipientsText} onChange={(e) => setRecipientsText(e.target.value)} rows={7}
                          className="w-full px-3 py-2 border border-ink-line/40 bg-white text-ink text-sm font-mono" data-testid="esign-bulk-recipients"
                          placeholder={"NorthStar Family Office <northstar@venturaz.com>\nBharat Growth Fund, bharat@venturaz.com\nhorizon@venturaz.com"} />
              <div className="text-[11px] text-ink-muted mt-1" data-testid="esign-bulk-count">Parsed: {parseRecipients().length} recipient(s) · CSV format: <code>name,email</code> (header auto-detected)</div>
              {pricing && (() => {
                const count = parseRecipients().length;
                const tiers = pricing.bulk_discount_tiers || [];
                let pct = 0;
                for (const t of tiers) if (count >= t.min) pct = t.pct;
                const perFull = Number(pricing.price_inr || 0);
                const perDisc = pct > 0 ? Math.max(1, Math.round(perFull * (100 - pct) / 100)) : perFull;
                const totalFull = perFull * count;
                const totalDisc = perDisc * count;
                const savings = totalFull - totalDisc;
                const nextTier = tiers.find((t) => t.min > count);
                if (!count) return null;
                if (pct > 0) {
                  return (
                    <div
                      className="mt-2 px-3 py-2 bg-gradient-to-r from-emerald-50 to-emerald-100 border border-emerald-200 rounded flex items-center justify-between text-sm gap-3 flex-wrap"
                      data-testid="esign-bulk-savings-ribbon"
                    >
                      <div>
                        <span className="text-emerald-800 font-heading">You save ₹{savings.toLocaleString("en-IN")}</span>
                        <span className="text-emerald-700 text-xs ml-2 font-mono">
                          {pct}% bulk discount · ₹{perDisc}/doc (was ₹{perFull})
                        </span>
                      </div>
                      <div className="text-xs font-mono text-emerald-700">
                        Total: <span className="line-through text-emerald-700/60 mr-1">₹{totalFull}</span>
                        <span className="font-heading text-emerald-900">₹{totalDisc}</span>
                        {nextTier && (
                          <span className="ml-3 text-emerald-700/80">
                            Add {nextTier.min - count} more for {nextTier.pct}% off
                          </span>
                        )}
                      </div>
                    </div>
                  );
                }
                if (nextTier) {
                  return (
                    <div
                      className="mt-2 px-3 py-2 bg-gold/10 border border-gold/30 rounded text-xs text-ink flex items-center justify-between gap-2 flex-wrap"
                      data-testid="esign-bulk-savings-hint"
                    >
                      <span>
                        Add <strong>{nextTier.min - count} more</strong> recipient{nextTier.min - count === 1 ? "" : "s"} to unlock a <strong>{nextTier.pct}%</strong> bulk discount.
                      </span>
                      <span className="font-mono text-ink-muted">Currently ₹{totalFull} total ({count} × ₹{perFull})</span>
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            <div className="pt-3 border-t border-ink-line/20">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Additional Clauses · optional</label>
                  <div className="text-[11px] text-ink-muted mt-0.5">Appended after the 20 standard clauses. Line breaks are preserved.</div>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={loadLibrary} className="text-xs text-ink hover:text-gold flex items-center gap-1" data-testid="esign-bulk-open-library">
                    <BookMarked size={12} /> Load from library
                  </button>
                  <button onClick={addClause} className="text-xs text-gold hover:text-gold/70 flex items-center gap-1" data-testid="esign-bulk-add-clause">
                    <Plus size={12} /> Add clause
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                {extraClauses.map((c, i) => (
                  <div key={i} className="border border-ink-line/30 bg-white/60 p-2 space-y-2" data-testid={`esign-bulk-clause-${i}`}>
                    <div className="flex items-center gap-2">
                      <input value={c.title} onChange={(e) => updateClause(i, "title", e.target.value)}
                              placeholder="Clause title (e.g. Publicity, Export Compliance)"
                              className="flex-1 px-2 py-1.5 border border-ink-line/40 bg-white text-sm" />
                      <button onClick={() => saveToLibrary(i)} title="Save this clause to your library"
                                className="text-ink-muted hover:text-gold" data-testid={`esign-bulk-save-clause-${i}`}>
                        <Save size={14} />
                      </button>
                      <button onClick={() => rmClause(i)} className="text-ink-muted hover:text-red-400" data-testid={`esign-bulk-rm-clause-${i}`}>
                        <X size={14} />
                      </button>
                    </div>
                    <textarea value={c.body} onChange={(e) => updateClause(i, "body", e.target.value)} rows={3}
                                placeholder="Clause body — plain text, line breaks preserved."
                                className="w-full px-2 py-1.5 border border-ink-line/40 bg-white text-sm" />
                  </div>
                ))}
                {extraClauses.length === 0 && (
                  <div className="text-[11px] text-ink-muted italic">No custom clauses. Standard 20-clause NDA will be used.</div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center gap-2 mt-6">
            <button onClick={preview} disabled={previewBusy} className="text-xs font-mono uppercase tracking-widest text-gold hover:text-gold/70 flex items-center gap-1 disabled:opacity-50" data-testid="esign-bulk-preview">
              <FileText size={12} /> {previewBusy ? "Building preview…" : "Preview NDA"}
            </button>
            <div className="flex items-center gap-2">
              <button onClick={onClose} className="text-xs text-ink-muted font-mono uppercase tracking-widest">Cancel</button>
              <Button onClick={submit} disabled={busy} data-testid="esign-bulk-submit">{busy ? "Sending…" : (autoSend ? "Generate & Send" : "Generate as Drafts")}</Button>
            </div>
          </div>
        </div>
      </div>

      {previewUrl && (
        <div className="fixed inset-0 bg-ink/85 z-[60] flex items-center justify-center p-6" onClick={closePreview}>
          <div className="bg-white w-full max-w-5xl h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="esign-bulk-preview-modal">
            <div className="flex items-center justify-between px-4 py-2 border-b border-ink-line/20 bg-ink text-white">
              <div className="overline text-gold">NDA Preview · Draft — not yet sent</div>
              <div className="flex items-center gap-3">
                <a href={previewUrl} target="_blank" rel="noreferrer" className="text-xs font-mono uppercase tracking-widest text-gold hover:text-gold/70 flex items-center gap-1">
                  <ExternalLink size={12} /> Open in tab
                </a>
                <button onClick={closePreview} className="text-white/70 hover:text-white" data-testid="esign-bulk-preview-close"><X size={16} /></button>
              </div>
            </div>
            <iframe title="NDA Preview" src={previewUrl} className="flex-1 w-full" data-testid="esign-bulk-preview-iframe" />
          </div>
        </div>
      )}

      {showTemplates && (
        <div className="fixed inset-0 bg-ink/70 z-[60] flex justify-end" onClick={() => setShowTemplates(false)}>
          <div className="bg-white/95 border-l border-gold/40 w-full max-w-md h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="esign-templates-panel">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="overline">Envelope Templates</div>
                  <div className="text-[11px] text-ink-muted mt-1">Load a preset (title, jurisdiction, term, clauses) into this modal. Seeds ship with VenturaZ.</div>
                </div>
                <button onClick={() => setShowTemplates(false)} className="text-ink-muted hover:text-ink"><X size={16} /></button>
              </div>
              {templates.length === 0 ? <div className="text-ink-muted text-sm">No templates yet.</div> : (
                <div className="space-y-2">
                  {templates.map((t) => (
                    <div key={t.template_id} className="border border-ink-line/30 bg-white p-3 space-y-2" data-testid={`esign-template-${t.template_id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="text-sm font-medium text-ink">{t.name}</div>
                            {t.is_seed && <span className="text-[9px] font-mono uppercase tracking-widest text-gold bg-gold/10 px-1.5 py-0.5">SEED</span>}
                          </div>
                          {t.description && <div className="text-[11px] text-ink-muted mt-1">{t.description}</div>}
                          <div className="text-[10px] text-ink-muted mt-1 font-mono uppercase tracking-wider">
                            {t.document_type} · {t.nda_params?.jurisdiction_city} · {t.nda_params?.term_years}y term
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button onClick={() => applyTemplate(t)}
                                    className="text-xs font-mono uppercase tracking-widest text-gold hover:text-gold/70"
                                    data-testid={`esign-template-apply-${t.template_id}`}>
                            Use
                          </button>
                          {!t.is_seed && (
                            <button onClick={() => deleteTemplate(t.template_id)}
                                      className="text-ink-muted hover:text-red-400"
                                      data-testid={`esign-template-delete-${t.template_id}`}>
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showLibrary && (
        <div className="fixed inset-0 bg-ink/70 z-[60] flex justify-end" onClick={() => setShowLibrary(false)}>
          <div className="bg-white/95 border-l border-gold/40 w-full max-w-md h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="esign-library-panel">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="overline">Clause Library</div>
                  <div className="text-[11px] text-ink-muted mt-1">Click Insert to add a clause to your NDA. Seed clauses ship with VenturaZ.</div>
                </div>
                <button onClick={() => setShowLibrary(false)} className="text-ink-muted hover:text-ink" data-testid="esign-library-close"><X size={16} /></button>
              </div>
              {(() => {
                // Group clauses by category preserving library order
                const groups = {};
                for (const c of library) {
                  const cat = c.category || "Other";
                  (groups[cat] = groups[cat] || []).push(c);
                }
                const catOrder = ["Confidentiality", "Compliance", "Diligence", "Commercial", "IP", "Data Protection", "Other"];
                const ordered = catOrder.filter((c) => groups[c]);
                if (library.length === 0) return <div className="text-ink-muted text-sm">No clauses in the library yet.</div>;
                return (
                  <div className="space-y-4">
                    {ordered.map((cat) => (
                      <div key={cat} data-testid={`esign-library-cat-${cat}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Filter size={11} className="text-ink-muted" />
                          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{cat}</div>
                          <div className="text-[10px] text-ink-muted">· {groups[cat].length}</div>
                        </div>
                        <div className="space-y-2">
                          {groups[cat].map((c) => (
                            <div key={c.clause_id} className="border border-ink-line/30 bg-white p-3 space-y-2" data-testid={`esign-library-clause-${c.clause_id}`}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <div className="text-sm font-medium text-ink">{c.title}</div>
                                    {c.is_seed && <span className="text-[9px] font-mono uppercase tracking-widest text-gold bg-gold/10 px-1.5 py-0.5">SEED</span>}
                                  </div>
                                  {c.tags?.length > 0 && (
                                    <div className="flex gap-1 mt-1 flex-wrap">
                                      {c.tags.map((t, ix) => <span key={ix} className="text-[9px] font-mono uppercase tracking-wider text-ink-muted bg-white/60 px-1.5 py-0.5">{t}</span>)}
                                    </div>
                                  )}
                                </div>
                                <button onClick={() => { insertFromLibrary(c); }}
                                          className="text-xs font-mono uppercase tracking-widest text-gold hover:text-gold/70 shrink-0"
                                          data-testid={`esign-library-insert-${c.clause_id}`}>
                                  Insert
                                </button>
                              </div>
                              <div className="text-[11px] text-ink-muted line-clamp-3 whitespace-pre-wrap">{c.body}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
